// deployment_class: customer_env_allowed
/**
 * CSA surface registry — the runtime UI-composition layer (Settings & Account
 * IA spec 2026-06-24 §4.2). Surfaces register themselves with a
 * {@link CsaSurfaceDeclaration}; the surface hub (Task 4) renders them by zone,
 * filtered by capability via {@link visibleSurfaces}.
 *
 * Pure, React-free, deterministic — so the composition logic is unit-testable
 * without rendering. The React hub consumes `visibleSurfaces(zone, scopes)`
 * with `useAuth().scopes`.
 *
 * Single source of truth for "what surfaces exist + where + in what order" —
 * the snap-together discipline (N30) at the UI runtime layer: a new surface is
 * ADDED by registering, never by editing a monolith page.
 */
import type { CsaSurfaceDeclaration, SurfaceZone } from './types';

/** Module-level registry. Keyed by surface id (unique). */
const registry = new Map<string, CsaSurfaceDeclaration>();

/**
 * Enroll a surface. Throws on a duplicate id — ids are unique and a silent
 * overwrite would be a setback (N68); registration is explicit + total.
 */
export function registerSurface(declaration: CsaSurfaceDeclaration): void {
  if (registry.has(declaration.id)) {
    throw new Error(
      `CsaSurface "${declaration.id}" already registered — surface ids must be unique`,
    );
  }
  registry.set(declaration.id, declaration);
}

/** Reset the registry (test helper; not used in app runtime). */
export function clearSurfaces(): void {
  registry.clear();
}

/** All surfaces in a zone, ascending by `order`. Does NOT filter by scope. */
export function getSurfaces(zone: SurfaceZone): CsaSurfaceDeclaration[] {
  return [...registry.values()]
    .filter((s) => s.zone === zone)
    .sort((a, b) => a.order - b.order);
}

/**
 * Surfaces in a zone the actor may SEE — `getSurfaces` filtered by capability.
 * A surface with no `requiredScope` is visible to any authenticated user; a
 * gated surface is shown only when the scope is present. Fails closed (an empty
 * scope set hides every gated surface) — this is the §9 layer-6 UI render-gate,
 * which sits ON TOP of the backend default-deny, never instead of it.
 */
export function visibleSurfaces(
  zone: SurfaceZone,
  scopes: readonly string[],
): CsaSurfaceDeclaration[] {
  return getSurfaces(zone).filter(
    (s) => s.requiredScope === undefined || scopes.includes(s.requiredScope),
  );
}

/** The distinct zones that currently have at least one registered surface. */
export function getZones(): SurfaceZone[] {
  return [...new Set([...registry.values()].map((s) => s.zone))];
}
